import axios, { AxiosError, InternalAxiosRequestConfig } from 'axios';

import { normalizeApiError } from './apiError';
import { createCoalescingGetAdapter } from './coalescingGetAdapter';
import { getDetectedCountry } from './countryService';
import { tokenService } from './tokenService';

const transportAdapter = axios.getAdapter(axios.defaults.adapter);
const axiosInstance = axios.create({
  baseURL: process.env.REACT_APP_API_URL,
  timeout: parseInt(process.env.REACT_APP_API_TIMEOUT || '10000'),
  headers: { 'Content-Type': 'application/json' },
  adapter: createCoalescingGetAdapter(transportAdapter),
});

async function addAuthHeader(config: InternalAxiosRequestConfig) {
  const token = tokenService.getAccessToken();

  if (token) config.headers.Authorization = `Bearer ${token}`;

  const country = await getDetectedCountry();

  if (country) config.headers['X-Client-Country'] = country;

  return config;
}

type RequestState = InternalAxiosRequestConfig & {
  _authRetry?: boolean;
  _safeRetryCount?: number;
};

const AUTH_NO_REFRESH = [
  '/auth/login', '/auth/email/login', '/auth/email/register', '/auth/register',
  '/auth/refresh-token', '/auth/callback',
];
const RETRYABLE_STATUS = new Set([429, 502, 503, 504]);
const isAuthMutation = (url?: string) => AUTH_NO_REFRESH.some((path) => url?.includes(path));
const isSafeMethod = (method?: string) => ['get', 'head', 'options'].includes((method || 'get').toLowerCase());
const wait = (milliseconds: number) => new Promise((resolve) => setTimeout(resolve, milliseconds));

const shouldRefreshToken = (error: AxiosError, request?: RequestState) => (
  !!request && error.response?.status === 401 && !request._authRetry && !isAuthMutation(request.url)
);

const retryAfterMilliseconds = (error: AxiosError, attempt: number): number => {
  const value = error.response?.headers?.['retry-after'];

  if (typeof value === 'string' && /^\d+$/.test(value)) return Number(value) * 1000;

  return [500, 1500][attempt] + Math.floor(Math.random() * 150);
};

const shouldRetrySafely = (error: AxiosError, request?: RequestState): request is RequestState => {
  if (!request || !isSafeMethod(request.method) || (request._safeRetryCount || 0) >= 2) return false;
  if (!error.response) return error.code === 'ECONNABORTED' || error.code === 'ETIMEDOUT' || error.code === 'ERR_NETWORK';

  return RETRYABLE_STATUS.has(error.response.status);
};

async function retrySafely(error: AxiosError, request: RequestState) {
  const attempt = request._safeRetryCount || 0;

  request._safeRetryCount = attempt + 1;
  await wait(retryAfterMilliseconds(error, attempt));

  return axiosInstance(request);
}

async function refreshAndRetry(request: RequestState) {
  request._authRetry = true;
  request.headers.Authorization = `Bearer ${await tokenService.refreshToken()}`;

  return axiosInstance(request);
}

// eslint-disable-next-line max-statements
function handleRefreshFailure(error: unknown) {
  const normalized = normalizeApiError(error);
  const hasNoRefreshToken = !tokenService.getRefreshToken();
  const sessionIsInvalid = hasNoRefreshToken || normalized.status === 401;

  // Keep valid local credentials during server/network outages. Only an explicit
  // refresh rejection means the session is no longer usable.
  if (sessionIsInvalid) {
    const hadTokens = !!tokenService.getAccessToken();

    tokenService.clearTokens();
    if (hadTokens && window.location.pathname !== '/') window.location.href = '/';
  }

  return Promise.reject(normalized);
}

// eslint-disable-next-line max-statements
const handleResponseError = async (value: unknown) => {
  if (!axios.isAxiosError(value)) return Promise.reject(normalizeApiError(value));
  const error = value as AxiosError;
  const request = error.config as RequestState | undefined;

  if (shouldRefreshToken(error, request)) {
    try {
      return await refreshAndRetry(request as RequestState);
    } catch (refreshError) {
      return handleRefreshFailure(refreshError);
    }
  }
  if (shouldRetrySafely(error, request)) return retrySafely(error, request);

  return Promise.reject(normalizeApiError(error));
};

axiosInstance.interceptors.request.use(addAuthHeader, (error) => Promise.reject(normalizeApiError(error)));
axiosInstance.interceptors.response.use((response) => response, handleResponseError);

export default axiosInstance;
